import { Component, Injector, OnInit } from '@angular/core';
import { SocialSharing } from '@ionic-native/social-sharing/ngx';
import { NavParams } from '@ionic/angular';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Feed, Like } from 'src/shared/models';
import { FeedService } from '../../services/feed/feed.service';
import { AddComponent } from '../add/add.component';
import { CommentComponent } from '../comment/comment.component';

@Component({
  selector: 'app-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss']
})
export class FeedDetailComponent extends Extender implements OnInit {
  public me: Account;
  public feed: Feed;
  public likes: Like[];
  public comments: Comment[];
  public liked: Like | any;

  /**
   * @description holds current index of image
   * @property currentIndex
   * @type number
   * @public
   * @default 0
   */
  public currentIndex: number = 0;
  constructor(
    protected injector: Injector,
    private navParams: NavParams,
    private socialSharing: SocialSharing,
    private feedService: FeedService
  ) {
    super(injector);
  }

  public async ngOnInit() {
    this.feed = this.navParams.get('data');
    this.me = await this.auth$.getUser();
  }

  public async openOptions(feed: Feed) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('Other.options'),
      buttons: [
        {
          text: this.translate.instant('Other.edit-label'),
          handler: () => {
            this.edit(feed);
          }
        },
        {
          text: this.translate.instant('Other.delete-label'),
          role: 'destructive',
          handler: () => {
            this.delete(feed);
          }
        },
        {
          text: this.translate.instant('FeedModule.open-comments'),
          handler: () => {
            this.comment(feed);
          }
        },
        {
          text: this.translate.instant('FeedModule.like'),
          handler: () => {
            this.like();
          }
        },
        {
          text: this.translate.instant('FeedModule.share-button'),
          handler: () => {
            this.share(feed);
          }
        },
        {
          text: this.translate.instant('Other.cancel'),
          role: 'cancel',
          handler: () => {}
        }
      ]
    });
    actionSheet.present();
  }

  public async edit(post: Feed) {
    if (post.contact.uid === this.me.uid) {
      const modal = await this.modalCtrl.create({
        component: AddComponent,
        componentProps: { data: post },
        cssClass: 'custom-modal'
      });
      await modal.present();
      modal.onDidDismiss().then((item: any) => {
        const feed = item.data;
        if (feed) {
          this.feedService.addUpdateFeed(feed).then();
        }
      });
    } else {
      this.toast(this.translate.instant('FeedModule.cannot-edit'));
    }
  }

  public like() {
    this.feedService.likeFeed(this.feed, this.me.uid);
  }

  public async comment(post): Promise<any> {
    const modal = await this.modalCtrl.create({
      component: CommentComponent,
      componentProps: { data: post }
    });
    modal.present();
  }

  public share(post: Feed) {
    this.socialSharing
      .share(post.content, post.title, post.images, post.subtitle)
      .then(() =>
        this.toast(this.translate.instant('FeedModule.share-confirm'))
      );
  }

  public async delete(post: Feed) {
    if (post.contact.uid === this.me.uid) {
      const prompt = await this.alertCtrl.create({
        header: this.translate.instant('FeedModule.delete-alert-title'),
        message: this.translate.instant('FeedModule.delete-alert-message'),
        buttons: [
          {
            text: this.translate.instant('Other.cancel'),
            role: 'cancel'
          },
          {
            text: this.translate.instant('Other.delete'),
            handler: () => {
              this.feedService.deleteFeed(post).then(() => {
                this.modalCtrl.dismiss();
                this.toast(this.translate.instant('FeedModule.confirm-delete'));
              });
            }
          }
        ]
      });
      await prompt.present();
    } else {
      this.toast(this.translate.instant('FeedModule.cannot-delete'));
    }
  }

  /**
   * @description navigate to the next image
   * @method forward
   * @public
   * @returns void
   */
  public forward(): void {
    if (this.currentIndex < this.feed.images.length - 1) {
      this.currentIndex += 1;
    } else {
      this.currentIndex = 0;
    }
  }

  /**
   * @description navigate to the previous image
   * @method back
   * @public
   * @returns void
   */
  public back(): void {
    if (this.currentIndex < 1) {
      this.currentIndex = this.feed.images.length - 1;
    } else {
      this.currentIndex -= 1;
    }
  }
}
